import { useEffect, useId, useMemo, useState } from "react"

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useQuerySchoolTransactionsSchoolData } from "api/reactQuery/queries/teacherAdmin"
import { useTranslation } from "react-i18next"

import { sortClassOptions } from "./dashboardFormat"

interface IClassOption {
  id: number
  name: string
}

interface IProps {
  classIds: number[]
  onChange: (ids: number[]) => void
  // Ids the loaded class list doesn't know, dropped without the admin asking.
  onStaleIds: (keptIds: number[]) => void
}

// Narrows the All students table to the ticked classes. The class list comes
// from `school-data`, fetched only once it's needed; until then the closed
// field counts the ids from the URL.
const ClassFilter = ({ classIds, onChange, onStaleIds }: IProps) => {
  const { t } = useTranslation()
  const [isOpened, setIsOpened] = useState(false)
  const valueId = useId()
  const query = useQuerySchoolTransactionsSchoolData({
    options: { enabled: isOpened || classIds.length > 0 },
  })
  const classes = query.data?.data.classes
  const options = useMemo(() => sortClassOptions(classes ?? []), [classes])
  const nextValue: IClassOption[] = classes
    ? options.filter((option) => classIds.includes(option.id))
    : classIds.map((id) => ({ id, name: "" }))
  // MUI empties the typed text whenever `value` is a new array, so it only
  // changes when the selection does.
  const valueKey = nextValue.map(({ id, name }) => `${id}:${name}`).join()
  const value = useMemo(() => nextValue, [valueKey])

  const idsKey = classIds.join(",")
  useEffect(() => {
    if (!classes) return
    const kept = classIds.filter((id) => classes.some((c) => c.id === id))
    if (kept.length < classIds.length) onStaleIds(kept)
  }, [classes, idsKey])

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, next) => onChange(next.map((option) => option.id))}
      onOpen={() => {
        setIsOpened(true)
        // "Try again" in the list can't be reached with Tab (it closes the
        // list), so reopening retries too.
        if (query.isError && !classes) query.refetch()
      }}
      // The closed field only counts the classes, so Backspace mustn't drop one
      // the admin can't see.
      onKeyDown={(event) => {
        if (event.key === "Backspace")
          (
            event as typeof event & { defaultMuiPrevented: boolean }
          ).defaultMuiPrevented = true
      }}
      getOptionLabel={(option) => option.name}
      // Class names can repeat, so the label can't be the key.
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={!classes && (!query.isError || query.isFetching)}
      loadingText={t("adminDashboard.loadingClasses")}
      noOptionsText={
        query.isError && !classes ? (
          <Stack alignItems="flex-start" rowGap={1}>
            {t("adminDashboard.classesError")}
            <Button
              variant="outlined"
              size="small"
              onClick={() => query.refetch()}
            >
              {t("adminDashboard.tryAgain")}
            </Button>
          </Stack>
        ) : options.length ? (
          t("adminDashboard.noMatchingClasses")
        ) : (
          t("adminDashboard.noClasses")
        )
      }
      renderOption={({ key, ...props }, option, { selected }) => (
        <Box component="li" key={key} {...props}>
          <Checkbox
            size="small"
            checked={selected}
            tabIndex={-1}
            disableRipple
            sx={{ p: 0, mr: 1 }}
          />
          {option.name}
        </Box>
      )}
      renderValue={(selected) => (
        <Typography
          id={valueId}
          variant="body2"
          noWrap
          sx={{ pl: 1, minWidth: 0, flexShrink: 1 }}
        >
          {selected.length === 1 && classes
            ? selected[0].name
            : t("adminDashboard.classCount", { count: selected.length })}
        </Typography>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={
            value.length ? undefined : t("adminDashboard.allClasses")
          }
          inputProps={{
            ...params.inputProps,
            "aria-label": t("adminDashboard.filterByClass"),
            // The closed field's summary sits beside the input, not in it.
            "aria-describedby": value.length ? valueId : undefined,
          }}
        />
      )}
      sx={{
        width: { mobile: "100%", tablet: 240 },
        flexShrink: 0,
      }}
    />
  )
}

export default ClassFilter
